import { useEffect, useState } from 'react';
import {
  getTripartiteContract,
  saveTripartiteContract,
  setTripartiteFlow,
  sendTripartiteEcpSms,
  resetTripartiteEcp,
  resetTripartiteContract,
} from '../../../../../api/deals/tripartiteContract';
import setStep from '../../../../../api/deals/setStep';
import uploadDocumentToDeal from '../../../../../api/deals/uploadDocumentToDeal';
import Button from '../../../../../components/Button/Button';
import LoaderInline from '../../../../../components/Loader/LoaderInline';
import { DOCUMENT_TYPES_TRIPARTITE_CONTRACT } from '../../../../../constants';
import { ROLE_ADMIN } from '../../../../../constants/roles';
import { colors } from '../../../../../constants/colors';
import { textStyles } from '../../../../../constants/textStyles';
import alert from '../../../../../helpers/alert';
import extractErrorMessage from '../../../../../utils/extractErrorMessage';
import isUserHaveRole from '../../../../../utils/isUserHaveRole';
import ConfirmModal from '../../../../../components/Modal/ConfirmModal';
import StepControlButtons from '../StepControlButtons';
import Document, { ROLE_LABEL } from './Document';
import ContractForm from './ContractForm';
import { FLOW_ECP, FLOW_PRINT, canGoNext, ecpSmsPhone, flowKey, focusFirstError, formErrors, formFromValues, regenerateWarning, restartEcpWarning, savePayload, switchFlowWarning } from './form';
import { Banner, card, hint, title } from './parts';
import styles from './TripartiteContractStep.module.css';

// Текст оверлея = сама операция: раньше один флаг saving показывал
// «Формируем договор…» и при сбросе подписей, и при переходе назад.
const BUSY_GENERATE = 'Формируем договор…';
const BUSY_RESET_ECP = 'Сбрасываем подписи…';
const BUSY_RESET_STEP = 'Сбрасываем шаг…';

const TripartiteContractStep = ({ deal, prevStep, nextStep, update }) => {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [state, setState] = useState(null);
  const [form, setForm] = useState(null);
  const [signedFile, setSignedFile] = useState(null);
  const [flow, setFlow] = useState(() => sessionStorage.getItem(flowKey(deal?.id)) || '');
  const [sendingSms, setSendingSms] = useState('');
  const [justGenerated, setJustGenerated] = useState(false);
  const [busyLabel, setBusyLabel] = useState('');
  const [navigating, setNavigating] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const askOrRun = (needAsk, dialog, run) => {
    if (!needAsk) {
      run();
      return;
    }
    setConfirm({ ...dialog, run });
  };

  // keepForm: опрос очереди ЭЦП идёт раз в 5 секунд, и перезаписывать им форму
  // нельзя — менеджер как раз правит в ней телефон или адрес.
  const applyResponse = (data, { keepForm = false } = {}) => {
    setState(data);
    if (!keepForm) setForm(formFromValues(data?.values || {}, data?.parties));
    if (data?.ecp) {
      const next = data.ecp.flow || '';
      setFlow(next);
      if (deal?.id) {
        if (next) sessionStorage.setItem(flowKey(deal.id), next);
        else sessionStorage.removeItem(flowKey(deal.id));
      }
    }
  };

  const load = () => {
    setLoading(true);
    setError('');
    getTripartiteContract(deal?.id)
      .then((res) => applyResponse(res?.data))
      .catch((e) => {
        setState({ available: false, loadError: true, forbidden: e?.response?.status === 403 });
        // 403 — это не сбой сервиса, а роль: без разделения менеджер видит
        // «Повторить», которое никогда не сработает.
        setError(
          e?.response?.status === 403
            ? 'Шаг доступен только сотрудникам ДЦ — обратитесь к администратору.'
            : 'Не удалось загрузить данные договора'
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, [deal?.id]);

  useEffect(() => {
    if (flow !== FLOW_ECP) return undefined;
    if (state?.ecp?.stage !== 'waiting') return undefined;
    const timer = setInterval(() => {
      getTripartiteContract(deal?.id)
        .then((res) => applyResponse(res?.data, { keepForm: true }))
        .catch(() => {});
    }, 5000);
    return () => clearInterval(timer);
  }, [flow, deal?.id, state?.ecp?.stage, state?.ecp?.signedCount]);

  const setField = (section, key, value) => {
    setForm((prev) => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
  };

  const { missing, invalid, blocked, isError } = formErrors(form, state?.missing, flow);

  const submit = () => {
    if (blocked.length) {
      const message = invalid.length ? 'Проверьте выделенные поля' : 'Заполните выделенные поля';
      setError(message);
      focusFirstError(blocked);
      return;
    }
    const warning = regenerateWarning(state?.ecp, Boolean(state?.document || state?.signedDocument));
    askOrRun(
      Boolean(warning),
      { title: 'Сформировать заново', text: warning, confirmText: 'Сформировать' },
      () => {
        setBusyLabel(BUSY_GENERATE);
        setError('');
        saveTripartiteContract(savePayload(deal?.id, form, { confirmWipe: Boolean(warning) }))
          .then((res) => {
            applyResponse(res?.data);
            const url = res?.data?.document?.url;
            if (url && flow === FLOW_PRINT) window.open(url, '_blank', 'noopener,noreferrer');
            setJustGenerated(true);
          })
          .catch((e) => {
            const data = e?.response?.data;
            const message = data?.errorMessage || extractErrorMessage(data) || 'Не удалось сформировать договор';
            setError(message);
            alert.error(message);
          })
          .finally(() => setBusyLabel(''));
      }
    );
  };

  const uploadSigned = () => {
    if (!signedFile) return;
    setUploading(true);
    setError('');
    uploadDocumentToDeal({
      dealId: deal?.id,
      fileTypeId: DOCUMENT_TYPES_TRIPARTITE_CONTRACT,
      file: signedFile,
      signed: true,
    })
      .then(() => getTripartiteContract(deal?.id))
      .then((res) => {
        applyResponse(res?.data);
        setSignedFile(null);
        alert.success('Скан загружен');
        update?.();
      })
      .catch((e) => {
        const data = e?.response?.data;
        const message = data?.errorMessage || extractErrorMessage(data) || 'Не удалось загрузить скан';
        setError(message);
        alert.error(message);
      })
      .finally(() => setUploading(false));
  };

  const sendEcp = (role) => {
    setSendingSms(role);
    sendTripartiteEcpSms({ dealId: deal?.id, role, phone: ecpSmsPhone(form, role) })
      .then((res) => {
        applyResponse(res?.data);
        alert.success(`СМС: ${ROLE_LABEL[role] || role}`);
      })
      .catch((e) => {
        const data = e?.response?.data;
        alert.error(data?.errorMessage || extractErrorMessage(data) || 'Не удалось отправить СМС');
      })
      .finally(() => setSendingSms(''));
  };

  const requestEcp = (role) => {
    const signer = (state?.ecp?.signers || []).find((item) => item.role === role);
    if (signer?.status !== 'sms') {
      sendEcp(role);
      return;
    }
    askOrRun(
      true,
      {
        title: 'Отправить СМС повторно',
        text: 'Клиенту придёт новая ссылка на подписание.',
        confirmText: 'Отправить',
      },
      () => sendEcp(role)
    );
  };

  const restartEcp = () => {
    askOrRun(
      true,
      { title: 'Сбросить подписи', text: restartEcpWarning(state?.ecp), confirmText: 'Сбросить' },
      () => {
        setBusyLabel(BUSY_RESET_ECP);
        resetTripartiteEcp({ dealId: deal?.id })
          .then((res) => {
            applyResponse(res?.data);
            alert.success('Подписи сброшены');
          })
          .catch((e) => {
            const data = e?.response?.data;
            alert.error(data?.errorMessage || extractErrorMessage(data) || 'Не удалось сбросить');
          })
          .finally(() => setBusyLabel(''));
      }
    );
  };

  const pickFlow = (next) => {
    const warning = switchFlowWarning(state?.ecp);
    askOrRun(
      Boolean(warning),
      { title: 'Сменить тип подписания', text: warning, confirmText: 'Сменить' },
      () => {
        setFlow(next);
        if (deal?.id) {
          if (next) sessionStorage.setItem(flowKey(deal.id), next);
          else sessionStorage.removeItem(flowKey(deal.id));
          setTripartiteFlow({ dealId: deal.id, flow: next || '' })
            .then((res) => applyResponse(res?.data))
            .catch((e) => {
              const data = e?.response?.data;
              alert.error(data?.errorMessage || extractErrorMessage(data) || 'Не удалось сменить тип');
            });
        }
      }
    );
  };

  const resetStep = () => {
    askOrRun(
      true,
      { title: 'Сбросить шаг', text: 'Документ и подписи будут удалены.', confirmText: 'Сбросить' },
      () => {
        setBusyLabel(BUSY_RESET_STEP);
        setError('');
        resetTripartiteContract({ dealId: deal?.id })
          .then((res) => {
            sessionStorage.removeItem(flowKey(deal?.id));
            setFlow('');
            setJustGenerated(false);
            setSignedFile(null);
            applyResponse(res?.data);
            alert.success('Шаг сброшен');
          })
          .catch((e) => {
            const data = e?.response?.data;
            const message = data?.errorMessage || extractErrorMessage(data) || 'Не удалось сбросить';
            setError(message);
            alert.error(message);
          })
          .finally(() => setBusyLabel(''));
      }
    );
  };

  const goNext = () => {
    if (!nextStep) return;
    setStep({ dealId: deal?.id, step: nextStep }).then(() => update());
  };

  if (loading) return <LoaderInline />;
  if (state?.loadError) {
    return (
      <div style={{ padding: '24px 20px' }}>
        <Banner tone="err">{error || 'Не удалось загрузить данные договора.'}</Banner>
        {state.forbidden ? null : <Button text="Повторить" fill onClick={load} />}
      </div>
    );
  }
  if (!state?.available) {
    return (
      <div style={{ padding: '24px 20px' }}>
        <p style={{ ...textStyles.formTitle, marginBottom: 16 }}>Авто не по договору поручения — этот шаг не нужен.</p>
        <Button disabled={!nextStep} text="Далее" fill onClick={goNext} />
      </div>
    );
  }
  if (!form) return <LoaderInline />;

  const isAdmin = isUserHaveRole(ROLE_ADMIN);
  const resetLink = isAdmin ? (
    <span
      role="button"
      onClick={resetStep}
      style={{ color: colors.dimGray, cursor: 'pointer', textDecoration: 'underline', fontSize: 14 }}
    >
      Сбросить шаг
    </span>
  ) : null;

  const overlayLabel = busyLabel || (navigating ? 'Переходим…' : '');
  const overlay = overlayLabel ? (
    <div className={styles.overlay}>
      <div className={styles.overlayContent}>
        <LoaderInline />
        <p style={{ ...hint, margin: '16px 0 0' }}>{overlayLabel}</p>
      </div>
    </div>
  ) : null;

  const confirmModal = (
    <ConfirmModal
      isOpen={!!confirm}
      onClose={() => setConfirm(null)}
      title={confirm?.title}
      text={confirm?.text}
      confirmText={confirm?.confirmText || 'Да'}
      cancelText="Отмена"
      onConfirm={() => {
        const run = confirm?.run;
        setConfirm(null);
        run?.();
      }}
    />
  );

  if (!flow) {
    const choice = {
      ...card,
      background: '#fff',
      cursor: 'pointer',
      marginBottom: 0,
      textAlign: 'left',
      font: 'inherit',
    };
    return (
      <div className={styles.form} style={{ padding: '24px 20px' }}>
        <StepControlButtons dealId={deal?.id} prevStep={prevStep} update={update} setLoading={setNavigating} />
        <p style={{ ...textStyles.formTitle, margin: '24px 0 4px' }}>Трёхсторонний договор</p>
        <p style={{ ...hint, marginBottom: 16 }}>
          купли-продажи автотранспортного средства и оказания агентских услуг
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <button type="button" style={choice} onClick={() => pickFlow(FLOW_PRINT)}>
            <p style={{ ...title, margin: 0 }}>Распечатать и подписать в офисе</p>
          </button>
          <button type="button" style={choice} onClick={() => pickFlow(FLOW_ECP)}>
            <p style={{ ...title, margin: 0 }}>Подписать ЭЦП</p>
          </button>
        </div>
        {resetLink && <p style={{ ...hint, margin: '16px 0 0' }}>{resetLink}</p>}
        {overlay}
        {confirmModal}
      </div>
    );
  }

  const busy = !!busyLabel || uploading || !!sendingSms;
  const file = state?.document || state?.signedDocument;
  let banner = null;
  if (error) banner = <Banner tone="err">{error}</Banner>;
  else if (justGenerated && state?.document)
    banner = (
      <Banner tone="ok">
        {flow === FLOW_PRINT
          ? `Договор № ${state?.values?.number} сформирован и открыт в новой вкладке.`
          : `Договор № ${state?.values?.number} сформирован.`}
      </Banner>
    );
  else if (!state?.document && blocked.length)
    banner = (
      <Banner tone="warn">
        {invalid.length ? 'Проверьте выделенные поля.' : `Не хватает ${missing.length} ${missing.length === 1 ? 'поля' : 'полей'}.`}
      </Banner>
    );

  return (
    <div className={styles.form} style={{ padding: '24px 20px' }}>
      <StepControlButtons dealId={deal?.id} prevStep={prevStep} update={update} setLoading={setNavigating} />
      <p style={{ ...textStyles.formTitle, margin: '24px 0 8px' }}>
        Трёхсторонний договор № E-{deal?.id}
        {state?.carTitle ? ` — ${state.carTitle}` : ''}
      </p>
      <p style={{ ...hint, marginBottom: 16 }}>
        {flow === FLOW_ECP ? 'Подписать ЭЦП' : 'Распечатать и подписать в офисе'}
        {' · '}
        <span
          role="button"
          onClick={() => pickFlow('')}
          style={{ color: colors.cameo, cursor: 'pointer', textDecoration: 'underline' }}
        >
          сменить тип подписания
        </span>
        {resetLink ? (
          <>
            {' · '}
            {resetLink}
          </>
        ) : null}
      </p>
      {banner}
      <ContractForm form={form} state={state} flow={flow} isError={isError} setField={setField} setForm={setForm} />
      <Document
        file={file}
        state={state}
        form={form}
        flow={flow}
        busy={busy}
        sendingSms={sendingSms}
        signedFile={signedFile}
        setSignedFile={setSignedFile}
        uploading={uploading}
        onSendEcp={requestEcp}
        onRestartEcp={restartEcp}
        onUploadSigned={uploadSigned}
      />
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
        {state?.ecp?.stage === 'complete' ? null : (
          <Button
            disabled={busy}
            text={busyLabel === BUSY_GENERATE ? 'Формируем…' : file ? 'Сформировать заново' : 'Сформировать договор'}
            fill
            onClick={submit}
          />
        )}
        <Button
          disabled={busy || !canGoNext({ flow, hasFile: Boolean(file), ecp: state?.ecp, nextStep })}
          text="Далее"
          fill
          onClick={goNext}
        />
      </div>
      {file && state?.ecp?.stage !== 'complete' && (state?.ecp?.signedCount > 0 || state?.ecp?.stage === 'waiting') ? (
        <p style={{ ...hint, marginTop: 8 }}>
          Процесс подписания начнётся заново.
        </p>
      ) : null}
      {overlay}
      {confirmModal}
    </div>
  );
};

export default TripartiteContractStep;
